// signalRService.ts
import * as signalR from '@microsoft/signalr';
import { BACKEND_HOST } from '../helpers/apiConfig';

class SignalRService {
	private connection: signalR.HubConnection | null = null;
	private joinedGroups: Set<string> = new Set();

	constructor() {
		this.startConnection();
	}

	private isStarting = false;

	private startConnection() {
		if (this.isStarting) return;
		this.isStarting = true;

		this.connection = new signalR.HubConnectionBuilder()
			.withUrl(`${BACKEND_HOST}/pushdownHub`)
			.withAutomaticReconnect()
			.configureLogging(signalR.LogLevel.Information)
			.build();

		this.connection.onreconnecting((error) => {
			console.warn('SignalR attempting to reconnect...', error?.message);
		});

		this.connection.onreconnected(async (connectionId) => {
			console.log('SignalR reconnected with connection ID:', connectionId);
			for (const group of this.joinedGroups) {
				try {
					await this.connection?.invoke('JoinGroup', group);
					console.log(`Rejoined group: ${group}`);
				} catch (err) {
					console.error(`Error rejoining group ${group}:`, err);
				}
			}
		});

		this.connection.onclose((error) => {
			console.error('SignalR connection closed:', error);
			setTimeout(() => this.startConnection(), 2000);
		});

		this.connection
			.start()
			.then(() => {
				console.log('SignalR Connected');
			})
			.catch((err: Error) => {
				console.error('SignalR Connection Error:', err);
				setTimeout(() => this.startConnection(), 5000);
			})
			.finally(() => {
				this.isStarting = false;
			});
	}

	// Method to add event listeners for SignalR events
	public on(event: string, callback: (...args: any[]) => void): void {
		this.connection?.off(event);
		this.connection?.on(event, callback);
	}

	// Method to remove event listeners for SignalR events
	public off(event: string, callback: (...args: any[]) => void): void {
		this.connection?.off(event, callback);
	}

	// Method to send data to the SignalR hub
	public send(event: string, data: unknown): Promise<void> | undefined {
		return this.connection?.invoke(event, data).catch((err: Error) => console.error('SignalR Send Error:', err));
	}

	// Method to join a specific group with retry mechanism
	public async joinGroup(groupName: string): Promise<void> {
		this.joinedGroups.add(groupName);
		const retryInterval = 1000; // 1 second
		const maxRetries = 5;
		let attempts = 0;

		const attemptJoin = async () => {
			if (this.connection?.state === signalR.HubConnectionState.Connected) {
				// If the connection is connected, attempt to join the group
				try {
					await this.connection.invoke('JoinGroup', groupName);
					console.log(`Joined group: ${groupName}`);
				} catch (err) {
					console.error('SignalR Join Group Error:', err);
				}
			} else if (attempts < maxRetries) {
				// Retry after a delay if the connection is not ready
				attempts++;
				console.warn(`Retrying to join group (${attempts}/${maxRetries})...`);
				setTimeout(attemptJoin, retryInterval);
			} else {
				console.error(`Failed to join group ${groupName} after ${maxRetries} attempts.`);
			}
		};

		await attemptJoin();
	}

	// Method to leave a specific group
	public leaveGroup(groupName: string): Promise<void> | undefined {
		this.joinedGroups.delete(groupName);

		return this.connection
			?.invoke('LeaveGroup', groupName)
			.then(() => console.log(`Left group: ${groupName}`))
			.catch((err: Error) => console.error('SignalR Leave Group Error:', err));
	}
}

export default new SignalRService();
